import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Building2, 
  ClipboardList, 
  GitCompare, 
  TrendingUp,
  TrendingDown,
  Eye,
  Plus,
  FolderOpen,
  Share2,
  ExternalLink,
  Copy,
  Settings
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import dashboardService from '../services/dashboardService';
import catalogService from '../services/catalogService';
import profileService from '../services/profileService';
import PermissionGate from '../components/PermissionGate';
import './DashboardPage.css';

const PUBLIC_SITE_ORIGIN = 'https://miskest.com';

const DashboardPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  // API state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [catalogSummary, setCatalogSummary] = useState({
    count: null,
    defaultCatalog: null,
    loaded: false
  });
  const [landingProfile, setLandingProfile] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [recentOffers, setRecentOffers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [recentRequests, setRecentRequests] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getStats();
      if (response.success) {
        setDashboardStats({
          ...response.data.stats,
          newRequestsToday: response.data.newRequestsToday || 0,
          newMatchesToday: response.data.newMatchesToday || 0,
          needsUpdateCount: response.data.needsUpdateCount || 0
        });
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const fetchLandingProfile = useCallback(async () => {
    try {
      const response = await profileService.getProfile();
      if (response.success) {
        setLandingProfile(response.data?.user || response.data || null);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchLandingProfile();
  }, [fetchLandingProfile]);

  const fetchCatalogSummary = useCallback(async () => {
    try {
      const response = await catalogService.getMyCatalogs({ page: 1, limit: 100 });
      if (response.success) {
        const catalogs = response.data?.catalogs || response.data || [];
        const defaultCatalog = catalogs.find((catalog) => catalog.isDefault || catalog.name === 'كافة العروض') || null;
        setCatalogSummary({
          count: catalogs.length,
          defaultCatalog,
          loaded: true
        });
      }
    } catch (_) {
      setCatalogSummary((prev) => ({ ...prev, loaded: true }));
    }
  }, []);

  useEffect(() => {
    fetchCatalogSummary();
  }, [fetchCatalogSummary]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 420);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Request creation now lives only on the protected /requests/new page.



  const stats = useMemo(() => [
    { 
      icon: Building2, 
      value: dashboardStats?.totalOffers || 0, 
      label: t('dashboard.totalOffers'),
      trend: `${dashboardStats?.changes?.offers >= 0 ? '+' : ''}${dashboardStats?.changes?.offers || 0}%`,
      positive: (dashboardStats?.changes?.offers || 0) >= 0,
      color: '#1a5f4a',
      link: '/offers'
    },
    { 
      icon: ClipboardList, 
      value: dashboardStats?.totalRequests || 0, 
      label: t('dashboard.totalRequests'),
      trend: `${dashboardStats?.changes?.requests >= 0 ? '+' : ''}${dashboardStats?.changes?.requests || 0}%`,
      positive: (dashboardStats?.changes?.requests || 0) >= 0,
      color: '#c4a35a',
      link: '/requests'
    },
    { 
      icon: GitCompare, 
      value: dashboardStats?.totalMatches || 0, 
      label: t('dashboard.newMatches'),
      trend: `${dashboardStats?.changes?.matches >= 0 ? '+' : ''}${dashboardStats?.changes?.matches || 0}`,
      positive: (dashboardStats?.changes?.matches || 0) >= 0,
      color: '#17a2b8',
      link: '/requests?tab=matching'
    },
    { 
      icon: Eye,
      value: dashboardStats?.needsUpdateCount || 0,
      label: 'تحتاج تحديث',
      trend: 'متابعة',
      positive: false,
      color: '#f59e0b',
      link: '/offers'
    }
  ], [t, dashboardStats]);

  const getLandingUsername = useCallback(() => {
    const directUsername = (
      landingProfile?.username ||
      landingProfile?.profile?.username ||
      user?.username ||
      user?.profile?.username ||
      user?.data?.username ||
      user?.user?.username
    );
    if (String(directUsername || '').trim()) return String(directUsername).trim();

    try {
      const stored = JSON.parse(localStorage.getItem('msk_user_data') || '{}');
      return String(stored?.username || stored?.profile?.username || '').trim();
    } catch (_) {
      return '';
    }
  }, [landingProfile, user]);

  const publicUsername = getLandingUsername();

  const landingPath = publicUsername ? `/p/${encodeURIComponent(publicUsername)}` : '';
  const landingFullUrl = landingPath ? `${PUBLIC_SITE_ORIGIN}${landingPath}` : '';

  const landingCompletion = useMemo(() => {
    // يقيس اكتمال الموقع من بيانات المستخدم الحالية فقط، بدون طلب API جديد.
    const services = Array.isArray(user?.services) ? user.services : [];
    const checks = [
      { done: Boolean(user?.heroImage || user?.heroImageUrl || user?.heroImageMode), label: 'صورة الغلاف' },
      { done: services.length > 0, label: 'الخدمات' },
      { done: Boolean(String(user?.districts || '').trim()), label: 'نطاق العمل' },
      { done: Boolean(user?.twitter || user?.instagram || user?.tiktok || user?.extraLink || user?.phone || user?.email), label: 'التواصل' },
      { done: Boolean(String(user?.bio || '').trim()), label: 'النبذة' }
    ];
    const completed = checks.filter((check) => check.done).length;
    const missing = checks.filter((check) => !check.done).map((check) => check.label);
    return { completed, total: checks.length, missing };
  }, [user]);

  const goToLandingSettings = useCallback(() => {
    navigate('/settings?tab=landing');
  }, [navigate]);

  const handlePreviewLanding = useCallback(() => {
    if (!landingPath) {
      toast.warning('أضف اسم مستخدم لموقعك العقاري أولاً من الإعدادات');
      goToLandingSettings();
      return;
    }

    window.open(landingPath, '_blank', 'noopener,noreferrer');
  }, [goToLandingSettings, landingPath, toast]);

  const handleCopyLandingLink = useCallback(async () => {
    if (!landingFullUrl) {
      toast.warning('أضف اسم مستخدم لموقعك العقاري أولاً من الإعدادات');
      goToLandingSettings();
      return;
    }

    try {
      await navigator.clipboard.writeText(landingFullUrl);
      toast.success('تم نسخ رابط موقعك العقاري');
    } catch (_) {
      toast.error('تعذر نسخ الرابط، انسخه يدوياً من البطاقة');
    }
  }, [goToLandingSettings, landingFullUrl, toast]);

  const handleShareLandingLink = useCallback(async () => {
    if (!landingFullUrl) {
      toast.warning('أضف اسم مستخدم لموقعك العقاري أولاً من الإعدادات');
      goToLandingSettings();
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'موقعي العقاري في مسك',
          url: landingFullUrl
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    await handleCopyLandingLink();
  }, [goToLandingSettings, handleCopyLandingLink, landingFullUrl, toast]);

  const defaultCatalogPath = useMemo(() => {
    const slug = catalogSummary.defaultCatalog?.slug;
    if (!slug) return '';
    return publicUsername
      ? `/p/${encodeURIComponent(publicUsername)}/catalog/${encodeURIComponent(slug)}`
      : `/c/${encodeURIComponent(slug)}`;
  }, [catalogSummary.defaultCatalog, publicUsername]);

  const handleCopyDefaultCatalogLink = useCallback(async () => {
    if (!defaultCatalogPath) return;

    try {
      await navigator.clipboard.writeText(`${PUBLIC_SITE_ORIGIN}${defaultCatalogPath}`);
      toast.success('تم نسخ رابط الكتالوج العام');
    } catch (_) {
      toast.error('تعذر نسخ رابط الكتالوج');
    }
  }, [defaultCatalogPath, toast]);

  const actionItems = useMemo(() => [
    {
      icon: Plus,
      label: 'إضافة عرض',
      // Keep Dashboard as an entry point to the one complete offer form.
      onClick: () => navigate('/offers/new', { state: { from: '/dashboard' } })
    },
    {
      icon: ClipboardList,
      label: 'إضافة طلب',
      // Dashboard enters the same complete standalone request form.
      onClick: () => navigate('/requests/new', { state: { from: '/dashboard' } })
    },
    {
      icon: Settings,
      label: 'تعديل موقعي العقاري',
      onClick: goToLandingSettings
    },
    {
      icon: ExternalLink,
      label: 'معاينة موقعي',
      onClick: handlePreviewLanding
    }
  ], [goToLandingSettings, handlePreviewLanding, navigate]);

  const scrollToDashboardTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  // recentOffers data now comes from API via fetchDashboardData

  // recentRequests data now comes from API via fetchDashboardData

  return (
    <PermissionGate feature="PAGE_DASHBOARD" type="page">
    <div className="dashboard-page">
      <div className="page-header">
        <div className="welcome-section">
          <h1>{t('dashboard.welcome')}، {user?.name?.split(' ')[0]} 👋</h1>
          <p>إليك ملخص نشاطك اليوم</p>
        </div>
        <div className="header-actions dashboard-header-actions">
          {actionItems.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.label} className="btn btn-primary dashboard-top-action" onClick={item.onClick}>
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="dashboard-operation-grid">
        <section className="dashboard-operation-card landing-operation-card">
          <div className="operation-card-header">
            <div>
              <h2>موقعي العقاري</h2>
              <p>شارك صفحتك مع العملاء لعرض خدماتك وعقاراتك وطرق التواصل معك.</p>
            </div>
            <div className="operation-icon">
              <Share2 size={24} />
            </div>
          </div>

          {landingCompletion.completed < landingCompletion.total && <div className="operation-progress">
            <span>اكتمال الموقع: {landingCompletion.completed} من {landingCompletion.total}</span>
            <div className="operation-progress-bar" aria-hidden="true">
              <div style={{ width: `${(landingCompletion.completed / landingCompletion.total) * 100}%` }} />
            </div>
            <small className="operation-missing-items">
              {landingCompletion.missing.length > 0
                ? `المتبقي: ${landingCompletion.missing.join('، ')}`
                : 'موقعك مكتمل'}
            </small>
          </div>}

          <div className={`landing-link-box ${landingPath ? '' : 'empty'}`}>
            <span>{landingPath || 'أضف اسم مستخدم لإنشاء رابط موقعك العقاري.'}</span>
          </div>

          <div className="operation-actions">
            {/* مشاركة الموقع تنسخ الرابط تلقائياً عندما لا يدعم الجهاز Web Share. */}
            <button type="button" className="btn btn-outline btn-sm" onClick={handleShareLandingLink}>
              <Share2 size={16} />
              مشاركة موقعي
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={handlePreviewLanding}>
              <ExternalLink size={16} />
              معاينة
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={goToLandingSettings}>
              <Settings size={16} />
              تعديل الموقع
            </button>
          </div>
        </section>

        <section className="dashboard-operation-card catalog-operation-card">
          <div className="operation-card-header">
            <div>
              <h2>كتالوجاتي</h2>
              <p>نظّم عروضك في كتالوجات وشاركها مع العملاء.</p>
            </div>
            <div className="operation-icon catalog-icon">
              <FolderOpen size={24} />
            </div>
          </div>

          <div className="operation-note">
            يمكنك إدارة كتالوجاتك ومشاركة عروضك مع العملاء من هنا.
          </div>

          {catalogSummary.count !== null && (
            <div className="catalog-summary">
              <span className="catalog-summary-label">عدد الكتالوجات</span>
              <strong>{catalogSummary.count}</strong>
            </div>
          )}

          <div className="operation-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/catalog')}>
              <FolderOpen size={16} />
              إدارة الكتالوجات
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate('/catalog')}>
              <Plus size={16} />
              إنشاء كتالوج مخصص
            </button>
            {defaultCatalogPath && (
              <button type="button" className="btn btn-outline btn-sm" onClick={handleCopyDefaultCatalogLink}>
                <Copy size={16} />
                نسخ رابط الكتالوج العام
              </button>
            )}
          </div>
        </section>
      </div>

      <div className="stats-grid" data-tour="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card" style={{ '--accent-color': stat.color, cursor: 'pointer' }} onClick={() => navigate(stat.link)}>
              <div className="stat-icon">
                <Icon size={28} />
              </div>
              <div className="stat-content">
                <h3>{stat.value}</h3>
                <p>{stat.label}</p>
                <div className={`stat-trend ${stat.positive ? 'positive' : 'negative'}`}>
                  {stat.positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  <span>{stat.trend}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* يركز هذا القسم على ما يحتاجه المسوق الآن بدل عرض ملخص عام فقط. */}
      <div className="dashboard-section today-section action-needed-section">
        <div className="section-header">
          <h2>
            <TrendingUp size={22} />
            مهام تحتاج انتباهك
          </h2>
        </div>
        <div className="action-needed-grid">
          <Link to="/offers" className="action-needed-card urgent">
            <div>
              <h3>عروض تحتاج تحديث</h3>
              <strong>{dashboardStats?.needsUpdateCount || 0}</strong>
            </div>
            <span>فتح العروض</span>
          </Link>
          <Link to="/requests" className="action-needed-card">
            <div>
              <h3>طلبات جديدة</h3>
              <strong>{dashboardStats?.newRequestsToday || 0}</strong>
            </div>
            <span>فتح الطلبات</span>
          </Link>
          <Link to="/requests?tab=matching" className="action-needed-card">
            <div>
              <h3>مطابقات جديدة</h3>
              <strong>{dashboardStats?.newMatchesToday || 0}</strong>
            </div>
            <span>عرض المطابقات</span>
          </Link>
          <button type="button" className="action-needed-card" onClick={goToLandingSettings}>
            <div>
              <h3>أكمل بيانات موقعك العقاري</h3>
              <strong>{landingCompletion.completed}/{landingCompletion.total}</strong>
            </div>
            <span>تعديل الموقع</span>
          </button>
        </div>
      </div>

      <div className="quick-actions" data-tour="quick-actions">
        <h3>إجراءات سريعة</h3>
        <div className="actions-grid compact-actions-grid">
          <button className="action-card" onClick={() => navigate('/offers/new', { state: { from: '/dashboard' } })}>
            <div className="action-icon">
              <Plus size={24} />
            </div>
            <span>إضافة عرض</span>
          </button>
          <button className="action-card" onClick={() => navigate('/requests/new', { state: { from: '/dashboard' } })}>
            <div className="action-icon">
              <ClipboardList size={24} />
            </div>
            <span>إضافة طلب</span>
          </button>
          <button className="action-card" onClick={() => navigate('/catalog')}>
            <div className="action-icon">
              <FolderOpen size={24} />
            </div>
            <span>إنشاء كتالوج مخصص</span>
          </button>
          <Link to="/requests?tab=matching" className="action-card">
            <div className="action-icon">
              <GitCompare size={24} />
            </div>
            <span>عرض المطابقات</span>
          </Link>
        </div>
      </div>

      {showBackToTop && (
        <button
          type="button"
          className="dashboard-back-to-top"
          onClick={scrollToDashboardTop}
          aria-label="العودة لأعلى الصفحة"
        >
          ↑
        </button>
      )}

    </div>
    </PermissionGate>
  );
};

export default DashboardPage;
